import { CalendarClock, ListChecks, Plus, X } from 'lucide-react';
import { useState } from 'react';

import { TaskCheckbox } from '@/components/tasks/TaskCheckbox';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';

import { MoveMenuItems } from '@/components/tasks/MoveActions';
import { Menu, MenuContent, MenuLabel, MenuTrigger } from '@/components/ui/menu';
import {
  useAddSubtask,
  useDeleteSubtask,
  useMoveTask,
  useToggleSubtask,
} from '@/hooks/useTasks';
import { formatDayShort, toDayString } from '@/lib/dates';
import { tasks as tasksApi } from '@/lib/api/endpoints';
import { keys } from '@/lib/queryClient';
import type { TaskDetail, TaskSummary } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

/**
 * The steps of a task.
 *
 * Deliberately a plain checklist, not a nested task list: steps have no date,
 * no priority and no screen of their own. They exist to answer one question —
 * is this task actually finished? — which is why an open step blocks the
 * parent's checkbox. The server enforces that regardless of what this shows.
 */
export function Subtasks({
  task,
  onOpenSubtask,
}: {
  task: TaskDetail;
  /** Opens a step as a task of its own — it has a description and labels too. */
  onOpenSubtask?: (taskId: string) => void;
}) {
  const t = useT();
  const moveTask = useMoveTask();
  const today = toDayString(new Date());
  const addSubtask = useAddSubtask(task.id);
  const toggleSubtask = useToggleSubtask(task.id);
  const deleteSubtask = useDeleteSubtask(task.id);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);

  // A step cannot itself have steps, so there is nothing to show.
  if (task.parent_task_id) return null;

  const done = task.subtask_done;
  const total = task.subtask_total;

  function submit() {
    const title = draft.trim();
    if (!title || addSubtask.isPending) return;
    addSubtask.mutate(
      { title },
      {
        onSuccess: () => {
          setDraft('');
          // Stay open: steps are almost always added several at a time.
        },
      },
    );
  }

  return (
    <section className="border-t border-border pt-4">
      <div className="mb-2.5 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
          {t('subtask.plural')}
        </h3>
        {total > 0 ? (
          <span
            className={cn(
              'rounded px-1.5 text-[11px] tabular-nums',
              done === total
                ? 'bg-accent-subtle text-accent'
                : 'bg-surface-hover text-content-muted',
            )}
          >
            {done}/{total}
          </span>
        ) : null}
      </div>

      {total > 0 ? (
        <ul className="mb-2 -mx-2">
          {task.subtasks.map((subtask) => {
            const completed = subtask.status === 'COMPLETED';
            return (
              <li
                key={subtask.id}
                className="group flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-surface hover:bg-surface-hover max-sm:min-h-12 max-sm:gap-3 max-sm:py-2 touch:min-h-12 touch:gap-3 touch:py-2"
              >
                <TaskCheckbox
                  checked={completed}
                  label={subtask.title}
                  disabled={toggleSubtask.isPending}
                  onToggle={() => toggleSubtask.mutate({ id: subtask.id, completed })}
                />
                <button
                  type="button"
                  onClick={() => onOpenSubtask?.(subtask.id)}
                  disabled={!onOpenSubtask}
                  title={onOpenSubtask ? t('subtask.open') : undefined}
                  className={cn(
                    'min-w-0 flex-1 truncate text-left text-sm max-sm:whitespace-normal max-sm:line-clamp-2 max-sm:text-[15px] touch:whitespace-normal touch:line-clamp-2 touch:text-[15px]',
                    completed ? 'text-content-subtle line-through' : 'text-content',
                    onOpenSubtask ? 'hover:underline underline-offset-2' : '',
                  )}
                >
                  {subtask.title}
                </button>

                {/* A step with a date of its own shows up on Today, next to
                    its parent's title. Without a date it stays here. */}
                {subtask.scheduled_date ? (
                  <span className="shrink-0 text-[11px] tabular-nums text-accent">
                    {formatDayShort(subtask.scheduled_date)}
                  </span>
                ) : null}

                <Menu>
                  <MenuTrigger
                    className={cn(
                      'rounded p-1 text-content-subtle transition-surface',
                      'hover:bg-surface-raised hover:text-content',
                      'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                      // No hover on a touch screen: always there, and 44px.
                      'max-sm:opacity-100 max-sm:p-2.5 touch:opacity-100 touch:p-2.5',
                    )}
                    aria-label={t('subtask.schedule', { title: subtask.title })}
                  >
                    <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                  </MenuTrigger>
                  <MenuContent align="end">
                    <MenuLabel>{t('task.move')}</MenuLabel>
                    <MoveMenuItems
                      defaultDate={subtask.scheduled_date ?? today}
                      onMove={(target, date) =>
                        moveTask.mutate({ taskId: subtask.id, target, date })
                      }
                    />
                  </MenuContent>
                </Menu>
                <button
                  type="button"
                  onClick={() => deleteSubtask.mutate(subtask.id)}
                  aria-label={t('subtask.delete', { title: subtask.title })}
                  className={cn(
                    'rounded p-1 text-content-subtle transition-surface',
                    'hover:bg-surface-raised hover:text-danger',
                    'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                    'max-sm:opacity-100 max-sm:p-2.5 touch:opacity-100 touch:p-2.5',
                  )}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {adding ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          className="flex items-center gap-2"
        >
          <input
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setDraft('');
                setAdding(false);
              }
            }}
            onBlur={() => {
              if (!draft.trim()) setAdding(false);
            }}
            placeholder={t('subtask.placeholder')}
            aria-label={t('subtask.title')}
            className={cn(
              'flex-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm',
              'max-sm:px-3 max-sm:py-2.5 max-sm:text-[15px] touch:px-3 touch:py-2.5 touch:text-[15px]',
              'text-content outline-none placeholder:text-content-subtle focus:border-accent',
            )}
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            disabled={!draft.trim() || addSubtask.isPending}
          >
            {t('common.add')}
          </Button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm max-sm:min-h-11 max-sm:text-[15px] touch:min-h-11 touch:text-[15px]',
            'text-content-subtle transition-surface hover:bg-surface-hover hover:text-content',
          )}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {t('subtask.add')}
        </button>
      )}
    </section>
  );
}

/** Compact progress for a task row: "1/3", tinted once everything is done. */
export function SubtaskProgress({
  done,
  total,
  blocked,
}: {
  done: number;
  total: number;
  blocked: boolean;
}) {
  const t = useT();
  if (total === 0) return null;
  return (
    <span
      className={cn(
        'flex items-center gap-0.5 text-xs tabular-nums',
        blocked ? 'text-content-subtle' : 'text-accent',
      )}
      title={
        blocked
          ? t('subtask.remaining', { count: total - done, total })
          : t('subtask.allDone')
      }
    >
      <ListChecks className="h-3 w-3" aria-hidden />
      {done}/{total}
    </span>
  );
}

/**
 * The unfinished steps of a task, shown under it on Today.
 *
 * A "1/3" badge says work remains but not *what* — and a task you cannot close
 * because of a step you have forgotten is exactly the thing this product exists
 * to keep visible. Only the open ones appear: the finished ones have nothing
 * left to tell you.
 *
 * Capped at three, because Today's job is to stay short.
 */
export function OpenSubtaskPreview({
  task,
  onToggle,
  max = 3,
}: {
  task: TaskSummary;
  onToggle: (subtask: TaskSummary) => void;
  max?: number;
}) {
  const t = useT();
  const { data: detail } = useQuery({
    queryKey: keys.task(task.id),
    queryFn: () => tasksApi.get(task.id),
    // Only fetched for tasks that actually have unfinished steps, so Today
    // costs one extra request per such task rather than one per row.
    enabled: task.subtask_total > task.subtask_done,
    staleTime: 30_000,
  });

  // A step with a date of its own is shown on that day as a row in its own
  // right, with its parent named underneath. Repeating it here as well puts the
  // same task on the screen twice, which reads as two things to do.
  const open = (detail?.subtasks ?? []).filter(
    (subtask) => subtask.status !== 'COMPLETED' && !subtask.scheduled_date,
  );
  if (open.length === 0) return null;

  const shown = open.slice(0, max);
  const hidden = open.length - shown.length;

  return (
    <ul className="mb-1 ml-[2.1rem] space-y-0.5 border-l border-border pl-3">
      {shown.map((subtask) => (
        <li key={subtask.id} className="flex items-center gap-2">
          <TaskCheckbox
            checked={false}
            label={subtask.title}
            onToggle={() => onToggle(subtask)}
            className="h-3.5 w-3.5 rounded-[4px]"
          />
          <span className="min-w-0 truncate text-xs text-content-muted">
            {subtask.title}
          </span>
        </li>
      ))}
      {hidden > 0 ? (
        <li className="pl-[1.35rem] text-xs text-content-subtle">
          {t('common.andMore', { count: hidden })}
        </li>
      ) : null}
    </ul>
  );
}
